// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Troublesome-clauses tab — KB-driven list of clauses flagged for legal /
 * procurement reasons (UCC references, broad warranties, etc.).
 */
import { type FC, useMemo, useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, XCircle } from 'lucide-react'
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { MarkdownViewer } from '@/components/ui/markdown-viewer'
import { SchemaFieldsView } from '@/subsystems/knowledge-base/shared/items/SchemaFieldsView'
import type { KbSchema } from '@/subsystems/knowledge-base/shared/types'

import type { TroublesomeClauseFinding } from '../../hooks/extractors'
import {
  EmptyState,
  PageLink,
  kbTitle,
  normalizeKbId,
  type NegotiationKbItem,
} from './shared'

interface ClausesTabProps {
  troublesomeClauses: TroublesomeClauseFinding[]
  troublesomeKbItems: NegotiationKbItem[]
  troublesomeKbSchema?: KbSchema | null
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}

type ClauseWithId = TroublesomeClauseFinding & { _annotationId: string }

interface ClauseGroup {
  kb: NegotiationKbItem
  clauses: ClauseWithId[]
}

export const ClausesTab: FC<ClausesTabProps> = ({
  troublesomeClauses,
  troublesomeKbItems,
  troublesomeKbSchema = null,
  onOpenPage,
  onOpenInDocument,
}) => {
  const clauseGroups = useMemo(
    () => groupClausesByKb(troublesomeClauses, troublesomeKbItems),
    [troublesomeClauses, troublesomeKbItems],
  )

  if (!clauseGroups.length) {
    return <EmptyState message="No troublesome clauses identified." />
  }

  return (
    <div className="divide-y" role="list" aria-label="Troublesome clauses">
      {clauseGroups.map(({ kb, clauses }) => (
        <ClauseKbItemRow
          key={kb.id}
          kb={kb}
          kbSchema={troublesomeKbSchema}
          clauses={clauses}
          onOpenPage={onOpenPage}
          onOpenInDocument={onOpenInDocument}
        />
      ))}
    </div>
  )
}

function groupClausesByKb(
  clauses: TroublesomeClauseFinding[],
  kbItems: NegotiationKbItem[],
): ClauseGroup[] {
  const map = new Map<string, ClauseWithId[]>()
  clauses.forEach((c, i) => {
    const tagged: ClauseWithId = { ...c, _annotationId: `clause-${i}` }
    const list = map.get(c.knowledgeItem) ?? []
    list.push(tagged)
    map.set(c.knowledgeItem, list)
  })

  const kbById = new Map<string, NegotiationKbItem>()
  for (const kb of kbItems) {
    kbById.set(kb.id, kb)
    const normalized = normalizeKbId(kb.id)
    if (normalized !== kb.id) kbById.set(normalized, kb)
  }

  return Array.from(map.entries()).map(([id, clausesForKb]) => {
    const kb = kbById.get(id) ?? kbById.get(normalizeKbId(id))
    return {
      kb: kb ?? { id, content: clausesForKb[0]?.issue ?? `KB ${id}` },
      clauses: clausesForKb,
    }
  })
}

const ClauseKbItemRow: FC<{
  kb: NegotiationKbItem
  kbSchema?: KbSchema | null
  clauses: ClauseWithId[]
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}> = ({ kb, kbSchema, clauses, onOpenPage, onOpenInDocument }) => {
  const [open, setOpen] = useState(true)
  const [kbModalOpen, setKbModalOpen] = useState(false)
  const hasClauses = clauses.length > 0
  const title = useMemo(() => kbTitle(kb.content), [kb.content])

  return (
    <div role="listitem">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className={`flex items-center gap-3 py-2 px-3 ${hasClauses ? 'hover:bg-muted/50' : ''} transition-colors`}>
          {hasClauses
            ? <XCircle className="h-4 w-4 text-red-500 shrink-0" aria-hidden="true" />
            : <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />}
          <button
            type="button"
            className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary font-mono shrink-0 cursor-pointer"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setKbModalOpen(true) }}
            aria-label={`View KB item ${kb.id}`}
          >
            KB#{kb.id}
          </button>
          <CollapsibleTrigger asChild disabled={!hasClauses}>
            <div
              className={`flex items-center gap-3 flex-1 min-w-0 ${hasClauses ? 'cursor-pointer' : ''}`}
              role="button"
              aria-expanded={hasClauses ? open : undefined}
              tabIndex={hasClauses ? 0 : undefined}
            >
              <span className="text-sm font-medium flex-1">{title}</span>
              {hasClauses ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs px-2 py-0 bg-red-100 text-red-800 border-red-200">
                    {clauses.length}
                  </Badge>
                  {open
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                </div>
              ) : (
                <span className="text-xs text-green-700">Not flagged</span>
              )}
            </div>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          {hasClauses && (
            <div className="ml-7 pb-2" role="list" aria-label={`Clauses for ${title}`}>
              {clauses.map((c, i) => (
                <ClauseRow
                  key={i}
                  clause={c}
                  onOpenPage={onOpenPage}
                  onOpenInDocument={
                    onOpenInDocument
                      ? () => onOpenInDocument(c._annotationId)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Dialog open={kbModalOpen} onOpenChange={setKbModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto z-10000">
          <DialogHeader>
            <DialogTitle>#{kb.id} — {title}</DialogTitle>
            <DialogDescription>Knowledge base criteria</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {kbSchema && kb.fields
              ? <SchemaFieldsView schema={kbSchema} fields={kb.fields} />
              : <MarkdownViewer content={kb.content} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const ClauseRow: FC<{
  clause: TroublesomeClauseFinding
  onOpenPage?: (page: number) => void
  onOpenInDocument?: () => void
}> = ({ clause, onOpenPage, onOpenInDocument }) => {
  const [open, setOpen] = useState(false)
  const sectionRef = clause.sectionReference

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center gap-3 py-1.5 px-3 rounded hover:bg-muted/50 cursor-pointer transition-colors"
          role="button"
          aria-expanded={open}
          tabIndex={0}
        >
          <span className="text-sm flex-1">
            {clause.summary || clause.issue || 'Flagged clause'}
          </span>
          {sectionRef && (
            <Badge variant="secondary" className="font-mono text-xs px-2 py-0">{sectionRef}</Badge>
          )}
          {clause.pageNumber != null && <PageLink page={clause.pageNumber} onOpen={onOpenPage} />}
          {onOpenInDocument && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenInDocument() }}
              className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary cursor-alias"
              title="View this clause in the document"
              aria-label="View this clause in the document"
            >
              View
            </button>
          )}
          {open
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-6 mr-3 mb-1 space-y-2 border-l-2 border-muted pl-3 py-1.5">
          <div>
            <p className="text-xs font-medium text-foreground mb-0.5">Quoted Text</p>
            <p className="text-sm text-foreground bg-muted/40 rounded px-2.5 py-1.5 leading-relaxed">{clause.quotedText}</p>
          </div>
          {clause.recommendedModification && (
            <div>
              <p className="text-xs font-medium text-foreground mb-0.5">Recommended Modification</p>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{clause.recommendedModification}</p>
            </div>
          )}
          {clause.reason && (
            <div>
              <p className="text-xs font-medium text-foreground mb-0.5">Reason</p>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{clause.reason}</p>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
